import { useCallback, useEffect, useState } from "react";
import { copyToClipboard, numberToCurrency, trackEvent } from "@lib/utils";
import { useAccount } from "wagmi";
import { CONNECT_WALLET, REFERRAL_LINK_COPIED } from "@constants/analytics";

import { Skeleton, useToast } from "@chakra-ui/react";
import { getDashboardata, getSeasonWisePoints } from "@services/index";

import { INITIAL_REWARDS_DATA } from "@constants/common";

import BaseImage from "@components/BaseImage";
import { RewardsGridFallback } from "@components/PointsDistribution-V2";
// import { KELP_GRAND_MILES_CONVERSION } from "@constants/analytics";
import useSanction from "@hooks/useSanction";
import { Button, Typography } from "@kelp-dao/kernel-design-system";
import DashboardBanner from "./DashboardBanner";
import Disconnected from "./Disconnected";
import { useConnectModal } from "@rainbow-me/rainbowkit";
import PageLoader from "@components/PageLoader";
// import { useQuery } from "@tanstack/react-query";

// import useKelpMiles from "@hooks/useKelpMiles";
const DashboardComponent = () => {
  const [dashboardData, setDasboardData] = useState<
    typeof INITIAL_REWARDS_DATA | null
  >(INITIAL_REWARDS_DATA);
  // const [isSeason1LabelOpen, setSeason1LabelOpen] = useState(false);
  const [isLoadingDashboardData, setIsLoadingDashboardData] = useState(false);
  const [seasonWisePoints, setSeasonWisePoints] = useState<any>(null);
  const [isLoadingSeasonPoints, setIsLoadingSeasonPoints] = useState(false);
  const [isInitialMount, setIsInitialMount] = useState(true);

  const { address } = useAccount();
  const toast = useToast();
  const { openConnectModal } = useConnectModal();

  // const { data: kernelStatsSeason1, isLoading: isLoadingSeason1Data } =
  //   useQuery({
  //     queryKey: ["kernel-season1-eth", address],
  //     queryFn: () => {
  //       return checkTokenEligibilityETH(address!);
  //     },
  //     enabled: address !== undefined,
  //   });

  // const kernelSeason1ResultETH = kernelStatsSeason1?.data;

  // const { data: kernelStatsSeason1BNB, isLoading: isLoadingSeason1DataBNB } =
  //   useQuery({
  //     queryKey: ["kernel-season1-bnb", address],
  //     queryFn: () => {
  //       return checkTokenEligibilityBNB(address!);
  //     },
  //     enabled: address !== undefined,
  //   });

  // const kernelSeason1ResultBNB = kernelStatsSeason1BNB?.data;

  // const isLoadingSeason1 = useMemo(() => {
  //   return isLoadingSeason1DataBNB || isLoadingSeason1Data;
  // }, [isLoadingSeason1Data, isLoadingSeason1DataBNB]);

  // const claimableSeason1Amount = useMemo(() => {
  //   const ethClaimable = kernelSeason1ResultETH?.data?.kernelPoints || 0;
  //   const bnbClaimable = kernelSeason1ResultBNB?.data?.kernelPoints || 0;

  //   return Number(ethClaimable) + Number(bnbClaimable);
  // }, [kernelSeason1ResultBNB, kernelSeason1ResultETH]);

  const handleConnectWallet = () => {
    openConnectModal?.();
    trackEvent(CONNECT_WALLET, {
      type: "dashboard",
      dapp: "kelp",
    });
  };

  const { isSanctioned } = useSanction();
  const getPoints = useCallback(async () => {
    if (address) {
      const result = await getDashboardata(address);
      setDasboardData(result?.data?.value || null);
      setIsLoadingDashboardData(false);
    }
  }, [address]);

  const getSeasonPoints = useCallback(async () => {
    if (address) {
      setIsLoadingSeasonPoints(true);
      try {
        const result = await getSeasonWisePoints(address);
        setSeasonWisePoints(result?.data || null);
      } catch (error) {
        setSeasonWisePoints(null);
      } finally {
        setIsLoadingSeasonPoints(false);
      }
    }
  }, [address]);

  // const getKelpValue = useMemo(() => {
  //   return (
  //     <Typography
  //       type="body"
  //       color="textPrimary"
  //       variant="medium"
  //       className="font-medium leading-4 text-[16px]"
  //       lineHeight="120%"
  //     >
  //       {dashboardData?.kelpMiles
  //         ? numberToCurrency((+dashboardData.kelpMiles / 1000).toFixed(1), 1)
  //         : "-"}
  //     </Typography>
  //   );
  // }, [dashboardData]);

  useEffect(() => {
    if (address) {
      setIsLoadingDashboardData(true);
      getPoints();
      getSeasonPoints();
    } else {
      setDasboardData(null);
      setSeasonWisePoints(null);
    }
  }, [address, getPoints, getSeasonPoints]);

  // Handle initial mount to prevent flash of disconnected screen
  useEffect(() => {
    // Give wagmi time to reconnect on initial mount
    const timer = setTimeout(() => {
      setIsInitialMount(false);
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  const getWalletConnectWarning = useCallback((text: string) => {
    return (
      <div className="text-text-primary bg-[#ECBB85] rounded-lg px-1 py-1 md:px-2 md:py-1.5 lg:px-3 lg:py-2 gap-1 md:gap-2 flex items-center max-w-fit">
        <div className="h-3 w-3 md:h-4 md:w-4 lg:h-5 lg:w-5 relative flex-shrink-0">
          <BaseImage src="/assets/info.svg" alt="info" fill />
        </div>

        <Typography
          type="body"
          variant="xsmall"
          className="font-normal leading-3 md:leading-4 lg:leading-5 text-[8px] md:text-[9px] lg:text-[10px] text-[#4A6A6A] whitespace-pre-line md:whitespace-pre-line lg:whitespace-nowrap"
        >
          {text}
        </Typography>
      </div>
    );
  }, []);

  const kelpMilesForReferrer = (() => {
    const value = Number(dashboardData?.kelpMilesForReferrer);

    if (typeof value === "number" && !isNaN(value)) {
      return numberToCurrency((value / 1000).toFixed(1), 1);
    }

    return "-";
  })();

  return (
    <>
      <div
        className={`flex flex-col gap-[24px] rounded-3xl max-w-full lg:max-w-[764px] xl:max-w-[930px] mx-auto ${
          isSanctioned
            ? "pointer-events-none opacity-50"
            : "pointer-events-auto opacity-100"
        }`}
      >
        <Typography
          type="heading"
          variant="xxsmall"
          className="font-semibold leading-5 text-[24px]"
          color="textPrimary"
        >
          My rewards
        </Typography>

        {isInitialMount && !address ? (
          <PageLoader />
        ) : address ? (
          <div className="flex gap-6 flex-col md:flex-row w-full">
            <div className="flex flex-col p-6 gap-[18px] rounded-[16px] bg-bg-card w-full">
              <div>
                <Typography
                  type="heading"
                  color="textPrimary"
                  variant="xxsmall"
                  className="font-medium leading-5 mb-2 "
                >
                  Your rewards
                </Typography>
                <Typography
                  type="body"
                  color={"textSecondary"}
                  variant="xsmall"
                  className="font-normal leading-4 text-[10px] text-[#4A6A6A]"
                  opacity={0.8}
                >
                  Kelp Grand Miles are now Kernel Points. 1 Kelp Grand Mile = 1
                  Kernel Point. Your overall points balance remains the same.
                </Typography>
              </div>

              <div
                className={`flex flex-col gap-4 ${!address ? "md:-mt-2" : ""}`}
              >
                {address ? (
                  seasonWisePoints || isLoadingSeasonPoints ? (
                    <div className="flex flex-col md:flex-col lg:flex-row gap-1">
                      <SeasonPointsCard
                        seasonLabel="Season 4 points"
                        points={seasonWisePoints?.season4KelpMiles}
                        dateText="1st Sep'25 to 31st Jan'26"
                        isLoading={isLoadingSeasonPoints}
                      />
                      <div className="border-t border-border-default my-[6px]"></div>
                      <SeasonPointsCard
                        seasonLabel="Season 3 points"
                        points={seasonWisePoints?.season3KMPoints}
                        dateText="ended on 31st Aug'25"
                        isLoading={isLoadingSeasonPoints}
                      />
                    </div>
                  ) : (
                    <SeasonPointsCard
                      seasonLabel="Total Kernel Points"
                      points={
                        dashboardData?.kelpMiles &&
                        !isNaN(Number(dashboardData?.kelpMiles))
                          ? Number(dashboardData?.kelpMiles)
                          : undefined
                      }
                      dateText="1st Sep'25 to 31st Jan'26"
                      isLoading={isLoadingDashboardData}
                    />
                  )
                ) : (
                  <div className="flex">
                    <SeasonPointsCard
                      seasonLabel="Season 4 points"
                      dateText="1st Sep'25 to 31st Jan'26"
                      isLoading={false}
                    />
                    <div className="flex items-center">
                      {getWalletConnectWarning(
                        "Connect wallet to\nview rewards"
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="flex flex-col justify-between p-6 gap-[18px] rounded-[16px] bg-bg-card w-full">
              <div>
                <Typography
                  type="heading"
                  color="textPrimary"
                  variant="xxsmall"
                  className="font-medium leading-5 mb-2"
                >
                  Referrals
                </Typography>
                <Typography
                  type="body"
                  color={"textSecondary"}
                  variant="xsmall"
                  className="font-normal leading-4 text-[10px] text-[#4A6A6A]"
                  opacity={0.8}
                  mt="4px"
                >
                  Earn upto 20% of Kernel Points accrued by your referrals
                </Typography>
              </div>
              <div className="flex items-start gap-3 mt-2">
                <div className="flex items-center justify-center h-full">
                  <BaseImage
                    src="/assets/kernel_new.svg"
                    width={24}
                    height={24}
                    alt="kelp"
                  />
                </div>
                <div className="flex flex-col flex-1">
                  <Typography
                    type="body"
                    color="Black"
                    variant="medium"
                    className="font-medium leading-4"
                    lineHeight="120%"
                  >
                    {address ? (
                      isLoadingDashboardData ? (
                        <Skeleton height="20px" w="60px" />
                      ) : (
                        kelpMilesForReferrer
                      )
                    ) : (
                      "-"
                    )}
                  </Typography>
                  <Typography
                    color="textSecondary"
                    type="heading"
                    variant="xsmall"
                    className="font-normal leading-4 text-[12px] text-[#4A6A6A] whitespace-pre-line"
                    lineHeight="120%"
                    mt="4px"
                  >
                    Kernel points
                  </Typography>
                </div>
                {!address && (
                  <div className="flex items-center">
                    {getWalletConnectWarning(
                      "Connect wallet to\nrefer your community"
                    )}
                  </div>
                )}
                {address && (
                  <div className="flex justify-end">
                    <Button
                      variant="outline"
                      size="small"
                      className="w-fit px-3 py-1 text-xs "
                      onClick={() => {
                        const referralUrl = `https://kelpdao.xyz/restake/?utm_source=${address}`;
                        copyToClipboard(referralUrl);
                        trackEvent(REFERRAL_LINK_COPIED, {
                          url: referralUrl,
                        });
                        toast({
                          description: "Referral link copied",
                          status: "success",
                          duration: 2000,
                          position: "top",
                          isClosable: false,
                        });
                      }}
                    >
                      <Typography
                        type="body"
                        variant="xsmall"
                        className="font-normal text-text-secondary leading-5"
                      >
                        Copy referral link
                      </Typography>
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : (
          <Disconnected onConnectWallet={handleConnectWallet} />
        )}

        <DashboardBanner />

        {address && (
          <RewardsGridFallback
            dashboardData={dashboardData}
            isLoading={isLoadingDashboardData}
          />
        )}
      </div>
    </>
  );
};

export default DashboardComponent;

interface SeasonPointsCardProps {
  seasonLabel: string;
  points?: number;
  dateText: string;
  isLoading: boolean;
}

const SeasonPointsCard = ({
  seasonLabel,
  points,
  dateText,
  isLoading,
}: SeasonPointsCardProps) => {
  return (
    <div className="flex items-start gap-1 flex-1">
      <div className="flex items-center justify-center h-full">
        <BaseImage
          src="/assets/kernel_new.svg"
          width={24}
          height={24}
          alt="kelp"
        />
      </div>
      {/* Mobile/Tablet: Row layout with 2 columns */}
      <div className="flex flex-row lg:hidden flex-1 justify-between items-center">
        <div className="flex flex-col gap-1">
          <Typography
            type="body"
            className="font-regular leading-4 text-[12px] text-[#4A6A6A]"
            lineHeight="120%"
            variant="medium"
          >
            {seasonLabel}
          </Typography>
          <Typography
            type="heading"
            color="textSecondary"
            opacity={0.8}
            variant="xsmall"
            className="font-normal leading-4 text-[10px] text-[#4A6A6A] whitespace-pre-line"
            lineHeight="140%"
          >
            {dateText}
          </Typography>
        </div>
        <div className="flex items-start">
          {isLoading ? (
            <Skeleton height="20px" w="60px" />
          ) : (
            <Typography
              type="body"
              color={"textSecondary"}
              className="font-medium leading-5 text-[16px]"
              lineHeight="140%"
            >
              {points ? numberToCurrency((points / 1000).toFixed(1), 1) : "-"}
            </Typography>
          )}
        </div>
      </div>
      {/* Desktop: Column layout - label, points, dateText */}
      <div className="hidden lg:flex flex-col flex-1">
        <Typography
          type="body"
          className="font-regular leading-4 text-[12px] text-[#4A6A6A]"
          lineHeight="120%"
          variant="medium"
        >
          {seasonLabel}
        </Typography>
        {isLoading ? (
          <Skeleton height="20px" w="60px" />
        ) : (
          <Typography
            type="body"
            color={"textSecondary"}
            className="font-medium leading-5 text-[16px]"
            lineHeight="140%"
          >
            {points ? numberToCurrency((points / 1000).toFixed(1), 1) : "-"}
          </Typography>
        )}
        <Typography
          type="heading"
          color="textSecondary"
          opacity={0.8}
          variant="xsmall"
          className="font-normal leading-4 text-[10px] text-[#4A6A6A] whitespace-pre-line"
          lineHeight="140%"
        >
          {dateText}
        </Typography>
      </div>
    </div>
  );
};
